import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Megaphone, Plus, MapPin, DollarSign, Users, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Marketplace() {
  const [listings, setListings] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ job_id: "", radius_km: 50, base_payout: 250, notes: "" });
  const load = () => {
    api.get("/marketplace/listings").then((r) => setListings(r.data));
    api.get("/jobs").then((r) => setJobs(r.data.filter((j) => j.status !== "completed").slice(0, 50)));
  };
  useEffect(() => { load(); }, []);

  const broadcast = async () => {
    if (!form.job_id) return toast.error("Select a job");
    try {
      await api.post("/marketplace/broadcast", form);
      toast.success("Broadcasted to marketplace");
      setOpen(false); load();
    } catch { toast.error("Broadcast failed"); }
  };
  const award = async (listing_id, bid_id) => {
    await api.post(`/marketplace/listings/${listing_id}/award/${bid_id}`);
    toast.success("Bid awarded"); load();
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="marketplace-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-600/15 border border-purple-500/20 flex items-center justify-center"><Megaphone className="w-6 h-6 text-purple-400" /></div>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-1">Operations · Outsourcing</div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Technician Marketplace</h1>
            <p className="text-sm text-slate-400 mt-1">Broadcast jobs to external/contractor technicians · Uber-style bidding</p>
          </div>
        </div>
        <Button data-testid="broadcast-btn" onClick={() => setOpen(true)} className="bg-purple-600 hover:bg-purple-500"><Plus className="w-4 h-4 mr-2" />Broadcast Job</Button>
      </div>
      {listings.length === 0 ? <div className="text-center text-slate-500 py-12 rounded-xl border border-slate-800 bg-[#0B1120]">No broadcasted jobs yet</div> :
      <div className="space-y-3">
        {listings.map((l) => (
          <div key={l.id} data-testid={`listing-${l.id}`} className="rounded-xl border border-slate-800 bg-[#0B1120] p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-base font-semibold">{l.vehicle}</div>
                <div className="text-xs font-mono text-slate-500 mt-0.5">{l.vin}</div>
              </div>
              <Badge className={l.status === "awarded" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/20" : "bg-amber-500/15 text-amber-300 border-amber-500/20"}>{l.status.toUpperCase()}</Badge>
            </div>
            <div className="flex gap-4 text-xs text-slate-400 mb-3">
              <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />Base: ${l.base_payout}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.radius_km}km radius</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(l.bids||[]).length} bids</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(l.broadcasted_at).toLocaleDateString()}</span>
            </div>
            {(l.bids||[]).length > 0 && (
              <div className="border-t border-slate-800 pt-3 space-y-1.5">
                <div className="text-[10px] uppercase tracking-wide font-bold text-slate-500">Bids</div>
                {l.bids.map((b) => (
                  <div key={b.id} className="flex items-center gap-3 rounded-lg bg-slate-900/40 border border-slate-800 p-2.5">
                    <div className="flex-1"><div className="text-sm">{b.bidder_name}</div><div className="text-[10px] text-slate-500">ETA {b.eta_hours}h · {b.notes}</div></div>
                    <div className="font-mono text-emerald-300 font-semibold">${b.bid_amount}</div>
                    {l.status !== "awarded" && <Button size="sm" onClick={() => award(l.id, b.id)} className="bg-emerald-600 hover:bg-emerald-500 h-7 text-xs">Award</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800">
          <DialogHeader><DialogTitle>Broadcast Job</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <select data-testid="broadcast-job-select" className="w-full bg-[#030712] border border-slate-800 rounded-lg p-2 text-sm" value={form.job_id} onChange={(e) => setForm({ ...form, job_id: e.target.value })}>
              <option value="">— Select Job —</option>
              {jobs.map((j) => <option key={j.id} value={j.id}>{j.vin} · {j.customer_name} · {j.status}</option>)}
            </select>
            <Input data-testid="broadcast-payout" type="number" placeholder="Base payout ($)" value={form.base_payout} onChange={(e) => setForm({ ...form, base_payout: parseFloat(e.target.value) || 0 })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="broadcast-radius" type="number" placeholder="Radius (km)" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: parseInt(e.target.value) || 50 })} className="bg-[#030712] border-slate-800" />
            <Input data-testid="broadcast-notes" placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#030712] border-slate-800" />
            <Button data-testid="broadcast-submit" onClick={broadcast} className="w-full bg-purple-600 hover:bg-purple-500">Broadcast</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
